import React, { useState, useEffect } from 'react';
import { commissionsAPI, usersAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Skeleton } from '../../components/ui/skeleton';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { BookOpen, Plus, Clock, User } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminCommissions() {
  const [commissions, setCommissions] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ title: '', brief: '', assigned_to: '', deadline: '', word_count: '', priority: 'standard' });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [comRes, staffRes] = await Promise.all([
        commissionsAPI.list({}),
        usersAPI.list({ limit: 100 }).catch(() => ({ data: { users: [] } }))
      ]);
      setCommissions(comRes.data.commissions || []);
      setStaff(staffRes.data.users || []);
    } catch { toast.error('Failed'); }
    finally { setLoading(false); }
  };

  const createCommission = async () => {
    if (!form.title) { toast.error('Title required'); return; }
    try {
      await commissionsAPI.create({ ...form, word_count: form.word_count ? parseInt(form.word_count) : null });
      toast.success('Commission created');
      setShowCreate(false); setForm({ title: '', brief: '', assigned_to: '', deadline: '', word_count: '', priority: 'standard' });
      loadData();
    } catch { toast.error('Failed'); }
  };

  const STATUS_COLORS = { assigned: '#1E5A8A', in_progress: '#9A6B1A', submitted: '#1F7A5A', completed: '#5B616E' };

  return (
    <div className="space-y-6" data-testid="admin-commissions-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><BookOpen className="w-6 h-6 text-[#B89B5E]" /> Story Commissions</h1>
          <p className="text-sm text-[#5B616E] mt-1">Commission and track story assignments</p>
        </div>
        <Button onClick={() => setShowCreate(true)} data-testid="create-commission-btn" className="bg-[#121316] text-white hover:bg-[#1B1D22]"><Plus className="w-4 h-4 mr-2" /> New Commission</Button>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{[1,2,3].map(i => <Skeleton key={i} className="h-40 rounded-xl" />)}</div>
      ) : commissions.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {commissions.map(c => (
            <Card key={c.id} className="bg-white border-[#E7E0D6]" data-testid="commission-card">
              <CardContent className="pt-5">
                <div className="flex items-start justify-between">
                  <div>
                    <Badge className="text-[10px]" style={{ background: `${STATUS_COLORS[c.status] || '#5B616E'}15`, color: STATUS_COLORS[c.status] || '#5B616E' }}>{c.status}</Badge>
                    <h3 className="font-medium mt-2">{c.title}</h3>
                    {c.brief && <p className="text-xs text-[#5B616E] mt-1 line-clamp-2">{c.brief}</p>}
                  </div>
                  <Badge variant="outline" className="text-[10px] capitalize">{c.priority}</Badge>
                </div>
                <div className="flex items-center gap-4 mt-3 pt-3 border-t border-[#E7E0D6] text-xs text-[#5B616E]">
                  {c.assigned_to_name && <div className="flex items-center gap-1"><User className="w-3 h-3" /> {c.assigned_to_name}</div>}
                  {c.deadline && <div className="flex items-center gap-1"><Clock className="w-3 h-3" /> {c.deadline}</div>}
                  {c.word_count && <span>{c.word_count} words</span>}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="text-center py-16 text-[#5B616E]"><BookOpen className="w-16 h-16 mx-auto mb-4 opacity-30" /><p>No commissions yet</p></div>
      )}

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle>New Story Commission</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><label className="text-sm font-medium mb-1 block">Title</label><Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} data-testid="commission-title-input" /></div>
            <div><label className="text-sm font-medium mb-1 block">Brief</label><Textarea value={form.brief} onChange={e => setForm({ ...form, brief: e.target.value })} placeholder="Story angle, key points..." /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><label className="text-sm font-medium mb-1 block">Assign To</label>
                <Select value={form.assigned_to} onValueChange={v => setForm({ ...form, assigned_to: v })}>
                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{staff.map(s => <SelectItem key={s.id} value={s.id}>{s.full_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><label className="text-sm font-medium mb-1 block">Priority</label>
                <Select value={form.priority} onValueChange={v => setForm({ ...form, priority: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{['standard', 'feature', 'urgent', 'breaking'].map(p => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><label className="text-sm font-medium mb-1 block">Deadline</label><Input type="date" value={form.deadline} onChange={e => setForm({ ...form, deadline: e.target.value })} /></div>
              <div><label className="text-sm font-medium mb-1 block">Word Count</label><Input type="number" value={form.word_count} onChange={e => setForm({ ...form, word_count: e.target.value })} /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={createCommission} className="bg-[#121316] text-white hover:bg-[#1B1D22]">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
